// path: src/Store.js
// Preview IAP Store — Featured, Packs, Boosts, Hearts, Treats, and Redeem.

import React, { useEffect, useMemo, useState } from "react";
import "./App.css";
import PackQuantityPrompt from "./Components/PackQuantityPrompt";
import StorePackRevealModal from "./Components/StorePackRevealModal";
import TreatIcon from "./Components/TreatIcon";
import {
    fetchStoreProducts,
    getIapStoreStatus,
    restoreStorePurchases,
} from "./utils/iapStore";
import { IAP_PRODUCT_CATEGORIES, getNoAdsProductId } from "./utils/iapCatalog";
import { MAX_HEARTS, formatHeartsMeter } from "./utils/heartEconomy";
import {
    CORGI_PACK_COST,
    TREATS_EARN_RATES_TBD,
    TREATS_EARN_RATES_TBD_COPY,
    TREATS_SHOP_KINDS,
    formatTreats,
    listTreatsShopItems,
} from "./utils/treats";
import { normalizeRedemptionCodeInput } from "./utils/redemptionCodes";

const STORE_TABS = [
    { id: "featured", label: "Featured" },
    { id: "packs", label: "Packs" },
    { id: "boosts", label: "Boosts" },
    { id: "hearts", label: "Hearts" },
    { id: "treats", label: "Treats" },
    { id: "redeem", label: "Redeem" },
];

const normalizeStoreTab = (value) => {
    const id = String(value || "").trim();
    if (id === "extras") {
        return "redeem";
    }
    return STORE_TABS.some((tab) => tab.id === id) ? id : "featured";
};

const Store = ({
    isAuthenticated = false,
    hearts = MAX_HEARTS,
    maxHearts = MAX_HEARTS,
    treats = 0,
    packBusy = false,
    packResult = null,
    remainingInventoryPacks = 0,
    adsRemoved = false,
    busyProductId = "",
    redeemBusy = false,
    redeemMessage = "",
    error = "",
    initialTab = "featured",
    onPurchaseProduct,
    onSpendTreats,
    onRedeemCode,
    onClosePackReveal,
    onOpenNextPack,
    onRestorePurchases,
}) => {
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [restoreMessage, setRestoreMessage] = useState("");
    const [redeemCodeInput, setRedeemCodeInput] = useState("");
    const [tab, setTab] = useState(() => normalizeStoreTab(initialTab));
    const [packQuantityOpen, setPackQuantityOpen] = useState(false);
    const status = getIapStoreStatus();
    const treatBalance = Math.max(0, Math.floor(Number(treats) || 0));
    const packModalOpen = Boolean(packResult?.drops?.length);
    const storeBusy = Boolean(busyProductId) || packBusy || packModalOpen;
    const treatsShopItems = listTreatsShopItems();

    useEffect(() => {
        setTab(normalizeStoreTab(initialTab));
    }, [initialTab]);

    const handleRedeemSubmit = async () => {
        if (typeof onRedeemCode !== "function") {
            return;
        }

        const ok = await onRedeemCode(redeemCodeInput);
        if (ok) {
            setRedeemCodeInput("");
        }
    };

    useEffect(() => {
        let cancelled = false;

        setLoading(true);
        fetchStoreProducts()
            .then((items) => {
                if (!cancelled) {
                    setProducts(items);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setProducts([]);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, []);

    const packProducts = useMemo(
        () =>
            products.filter(
                (product) => product.category === IAP_PRODUCT_CATEGORIES.PACKS
            ),
        [products]
    );
    const boostProducts = useMemo(
        () =>
            products.filter(
                (product) => product.category === IAP_PRODUCT_CATEGORIES.BOOSTS
            ),
        [products]
    );
    const heartProducts = useMemo(
        () =>
            products.filter(
                (product) => product.category === IAP_PRODUCT_CATEGORIES.HEARTS
            ),
        [products]
    );

    const handleRestore = async () => {
        setRestoreMessage("");
        try {
            const result = await restoreStorePurchases();
            if (typeof onRestorePurchases === "function") {
                onRestorePurchases(result);
            }
            if (result.restored.length) {
                setRestoreMessage(
                    `Restored ${result.restored.length} item(s).`
                );
                return;
            }
            setRestoreMessage(
                status.isPreview
                    ? "Nothing to restore. Packs and hearts are consumable; No Ads restore needs a live store account."
                    : "Nothing to restore — packs and heart packs are consumable."
            );
        } catch {
            setRestoreMessage("Restore failed. Try again later.");
        }
    };

    const renderProductCard = (product) => {
        const isNoAds = product.id === getNoAdsProductId();
        const owned = isNoAds && adsRemoved;
        const busy = busyProductId === product.id;

        return (
            <div key={product.id} className="storeProductCard">
                <div className="storeProductCard__copy">
                    <div className="storeProductCard__title">
                        {product.title}
                        {owned ? " · Owned" : ""}
                    </div>
                    <p className="storeProductCard__description">
                        {product.description}
                    </p>
                </div>
                <button
                    type="button"
                    className="homePrimaryButton"
                    disabled={storeBusy || owned}
                    onClick={() => onPurchaseProduct(product.id)}
                >
                    {busy
                        ? "Buying…"
                        : owned
                          ? "Owned"
                          : product.isPreview
                            ? `${product.priceLabel} · Preview`
                            : product.priceLabel}
                </button>
            </div>
        );
    };

    const renderTreatsShopCard = (item) => {
        const isPack = item.kind === TREATS_SHOP_KINDS.PACKS || item.packs > 0;
        const canAffordOne = isPack
            ? treatBalance >= CORGI_PACK_COST
            : treatBalance >= item.cost;
        const openingPack = packBusy || packModalOpen;

        return (
            <div key={item.id} className="storeProductCard">
                <div className="storeProductCard__copy">
                    <div className="storeProductCard__title">{item.title}</div>
                    <p className="storeProductCard__description">
                        {item.description}
                    </p>
                </div>
                <button
                    type="button"
                    className="homePrimaryButton"
                    disabled={
                        storeBusy ||
                        (!isPack && !canAffordOne) ||
                        typeof onSpendTreats !== "function"
                    }
                    onClick={() => {
                        if (isPack && item.askQuantity) {
                            setPackQuantityOpen(true);
                            return;
                        }
                        onSpendTreats(item.id);
                    }}
                >
                    {openingPack && isPack
                        ? "Adding…"
                        : isPack
                          ? `Spend · ${formatTreats(CORGI_PACK_COST)}`
                          : canAffordOne
                            ? `Spend · ${formatTreats(item.cost)}`
                            : `Need ${formatTreats(item.cost)}`}
                </button>
            </div>
        );
    };

    const renderTabBody = () => {
        if (tab === "featured") {
            const noAdsId = getNoAdsProductId();
            const featuredProducts = [
                ...boostProducts.slice(0, 1),
                ...products.filter((product) => product.id === noAdsId),
            ];

            return (
                <div className="friendsPanel">
                    <h2 className="friendsSectionTitle">Featured</h2>
                    <p className="friendsBody">
                        Spotlight bonuses and special offers. Seasonal sales will
                        show here too.
                    </p>
                    {loading ? (
                        <p className="friendsBody">Loading products…</p>
                    ) : featuredProducts.length > 0 ? (
                        <div className="storeProductList storeProductList--featured">
                            {featuredProducts.map(renderProductCard)}
                        </div>
                    ) : (
                        <p className="friendsBody">
                            Featured offers appear here when available.
                        </p>
                    )}

                    <div className="storeRestoreRow">
                        <button
                            type="button"
                            className="homePrimaryButton homeSecondaryButton"
                            onClick={handleRestore}
                            disabled={storeBusy}
                        >
                            Restore Purchases
                        </button>
                        {restoreMessage ? (
                            <p className="storeRestoreRow__message">
                                {restoreMessage}
                            </p>
                        ) : null}
                    </div>
                </div>
            );
        }

        if (tab === "packs") {
            return (
                <div className="friendsPanel">
                    <h2 className="friendsSectionTitle">Corgi Packs</h2>
                    <p className="friendsBody">
                        Buy packs and they&apos;ll be added to your Inventory on
                        Profile.
                    </p>
                    {loading ? (
                        <p className="friendsBody">Loading products…</p>
                    ) : (
                        <div className="storeProductList storeProductList--packs">
                            {packProducts.map(renderProductCard)}
                        </div>
                    )}
                </div>
            );
        }

        if (tab === "boosts") {
            return (
                <div className="friendsPanel">
                    <h2 className="friendsSectionTitle">Boosts</h2>
                    <p className="friendsBody">
                        Buy a Boost Bundle for Reveal, Shield, and Freeze
                        — usable during Story and Endless runs.
                    </p>
                    {loading ? (
                        <p className="friendsBody">Loading products…</p>
                    ) : (
                        <div className="storeProductList storeProductList--packs">
                            {boostProducts.map(renderProductCard)}
                        </div>
                    )}
                </div>
            );
        }

        if (tab === "hearts") {
            return (
                <div className="friendsPanel">
                    <h2 className="friendsSectionTitle">Hearts</h2>
                    <p className="friendsBody">
                        Add hearts to your stash. Can go above the natural
                        regen limit.
                    </p>
                    {loading ? (
                        <p className="friendsBody">Loading products…</p>
                    ) : (
                        <div className="storeProductList">
                            {heartProducts.map(renderProductCard)}
                        </div>
                    )}
                </div>
            );
        }

        if (tab === "treats") {
            return (
                <div className="friendsPanel">
                    <h2 className="friendsSectionTitle">Spend Treats</h2>
                    <p className="friendsBody">
                        Treats are earned from play and cannot be purchased.
                        Spend them on hearts, Corgi Packs, or run boosts.
                    </p>
                    {TREATS_EARN_RATES_TBD ? (
                        <div
                            className="storePreviewBanner storePreviewBanner--tbd"
                            role="note"
                        >
                            {TREATS_EARN_RATES_TBD_COPY}
                        </div>
                    ) : null}
                    <div className="storeProductList storeProductList--packs">
                        {treatsShopItems.map(renderTreatsShopCard)}
                    </div>
                </div>
            );
        }

        return (
            <div className="friendsPanel">
                <h2 className="friendsSectionTitle">Redeem</h2>
                <div className="storeRedeemPanel">
                    <p className="friendsBody">
                        Redemption codes are account-bound and require sign-in.
                    </p>
                    {isAuthenticated ? (
                        <>
                            <div className="storeRedeemRow">
                                <input
                                    type="text"
                                    value={redeemCodeInput}
                                    onChange={(event) =>
                                        setRedeemCodeInput(
                                            normalizeRedemptionCodeInput(
                                                event.target.value
                                            )
                                        )
                                    }
                                    className="storeRedeemInput"
                                    placeholder="ENTER CODE"
                                    autoCapitalize="characters"
                                    autoCorrect="off"
                                    spellCheck={false}
                                    disabled={storeBusy || redeemBusy}
                                    aria-label="Redemption code"
                                />
                                <button
                                    type="button"
                                    className="homePrimaryButton homeSecondaryButton storeRedeemButton"
                                    disabled={
                                        storeBusy ||
                                        redeemBusy ||
                                        !redeemCodeInput.trim() ||
                                        typeof onRedeemCode !== "function"
                                    }
                                    onClick={handleRedeemSubmit}
                                >
                                    {redeemBusy ? "Redeeming…" : "Redeem"}
                                </button>
                            </div>
                            {redeemMessage ? (
                                <p className="storeRedeemMessage" role="status">
                                    {redeemMessage}
                                </p>
                            ) : null}
                        </>
                    ) : (
                        <p className="storeRedeemMessage">
                            Sign in to claim Firestore-managed promo rewards.
                        </p>
                    )}
                </div>
            </div>
        );
    };

    return (
        <>
            <div className="storeScreen">
                <div className="friendsHeader">
                    <div>
                        <h1 className="impactFont pageTitle friendsTitle">
                            Store
                        </h1>
                        <p className="friendsBody" style={{ marginBottom: 0 }}>
                            Buy packs and hearts, or spend Treats you earned.
                            Treats cannot be purchased.
                        </p>
                    </div>
                </div>

                {status.isPreview ? (
                    <div className="storePreviewBanner" role="status">
                        Open Beta preview — purchases are free test grants. No
                        real charges until billing is live.
                    </div>
                ) : null}

                <div className="storeStatus" role="status">
                    <div className="storeStatus__row">
                        <div
                            className="storeStatus__hearts"
                            aria-label={`${Math.max(0, Math.floor(Number(hearts) || 0))} hearts`}
                        >
                            {formatHeartsMeter(hearts, maxHearts)}
                        </div>
                        <div
                            className="storeStatus__treats"
                            aria-label={formatTreats(treatBalance)}
                        >
                            <TreatIcon className="storeStatus__treatIcon" title="" />
                            {formatTreats(treatBalance)}
                        </div>
                    </div>
                </div>

                {error ? (
                    <p className="storeError" role="alert">
                        {error}
                    </p>
                ) : null}

                <div
                    className="friendsTabs storeTabs"
                    role="tablist"
                    aria-label="Store sections"
                >
                    {STORE_TABS.map((item) => (
                        <button
                            key={item.id}
                            type="button"
                            role="tab"
                            aria-selected={tab === item.id}
                            className={`friendsTab${
                                tab === item.id ? " friendsTab--active" : ""
                            }`}
                            onClick={() => setTab(item.id)}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>

                {renderTabBody()}
            </div>

            <StorePackRevealModal
                key={packResult?.openedAt || "pack-closed"}
                packResult={packResult}
                remainingInventoryPacks={remainingInventoryPacks}
                onClose={onClosePackReveal}
                onOpenNextPack={onOpenNextPack}
                onBuyMore={() => {
                    setTab("packs");
                    if (typeof onClosePackReveal === "function") {
                        onClosePackReveal();
                    }
                }}
            />

            {packQuantityOpen ? (
                <PackQuantityPrompt
                    treatBalance={treatBalance}
                    onCancel={() => setPackQuantityOpen(false)}
                    onConfirm={(packCount) => {
                        setPackQuantityOpen(false);
                        if (typeof onSpendTreats === "function") {
                            onSpendTreats("treats_shop_pack_1", { packCount });
                        }
                    }}
                />
            ) : null}
        </>
    );
};

export default Store;
